import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { GuruBotSocket, spotSocket } from 'src/app/app.module';
import { fraction, longDecimals, nFormatter, toFixedDown, truncateDecimals } from 'src/app/lib/roundOf';
import { adminGuruService } from 'src/app/services/bot.adminGuru.service';
import { WalletService } from 'src/app/services/wallet.service';
import { AuthFacade } from 'src/app/store/auth';
import { TradeAssetFacade } from 'src/app/store/bot-trade-asset';
import { PairFacade } from 'src/app/store/botpair';
import { PositionDetailsFacade } from 'src/app/store/positionDetails';
import { ToastrService } from 'ngx-toastr';
import isEmpty from 'src/app/lib/isEmpty';
import { HttpEventType, HttpEvent } from '@angular/common/http';
import { dateHours, dateHoursClose, dateHourswithTime, dateTimeFormat } from 'src/app/lib/dateFilter';

@Component({
  selector: 'app-adminBotGuruTopGainer',
  templateUrl: './TopGainer.component.html',
  styleUrls: ['./TopGainer.component.css']
})
export class AdminBotGuruTopGainerComponent implements OnInit {

  onWheel(event: WheelEvent) {
    event.preventDefault(); // Prevents the default scroll behavior
  }

  // Helpers
  toFixedDown = toFixedDown;
  truncateDecimals = truncateDecimals;
  longDecimals = longDecimals;
  fraction = fraction;
  nFormatter = nFormatter;
  dateTimeFormat = dateTimeFormat
  dateHoursClose = dateHoursClose;
  // Data State
  topGainers: any[] = [];
  recentTrade: any = [];
  auth: any = {};
  pairs: any = [];

  // Selection & UI State
  searchPair: string = '';
  activePair: string = '';
  selectedPair: any = null;
  investmentAmount: any | null = null;
  serverErrors: any = {}
  isDownloading = false;
  downloadProgress = 0
  submitLoader: boolean = false;
  changeSort: 'gainers' | 'losers' | '' = '';

  constructor(
    private pairFacade: PairFacade,
    private router: Router,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private location: Location,
    private service: adminGuruService,
    private authFacade: AuthFacade,
    private walletService: WalletService,
    private tradeAssetFacade: TradeAssetFacade,
    private positionDetails: PositionDetailsFacade,
    private spotSocket: spotSocket,
    private botSocket: GuruBotSocket
  ) {
    this.authFacade.auth$.subscribe({
      next: (userAuth: any) => {
        this.auth = userAuth;
      },
      error: (err: any) => { }
    });
  }

  ngOnInit() {
    this.getPairs();
    this.getRecentGainer()
    // Handle URL parsing safely
    const currentPath = this.location.path();
    if (currentPath) {
      const parts = currentPath.split('/');
      if (parts.length > 2) {
        this.activePair = parts[2];
      }
    }

    // Socket Listener
    this.botSocket.on('VMAP_Pair', (data: any) => {
      this.processTopGainersUpdate({ type: 'UPDATE_OR_ADD_PAIR', payload: data });
    });

    this.botSocket.on('marketPrice', (data: any) => {
      this.processTopGainersUpdate({ type: 'UPDATE_MARKET_PRICE', payload: data });
    });
  }

  /**
 * This is now the ONLY function that can change 'this.topGainers'.
 * It acts as a central gatekeeper, solving the race condition.
 */
  processTopGainersUpdate(action: { type: string, payload: any }) {
    // Read the current list ONCE.
    const currentList = this.topGainers;
    let newList;

    // Decide what to do based on the action type
    switch (action.type) {
      case 'UPDATE_OR_ADD_PAIR':
        newList = this._handleVmPairUpdate(currentList, action.payload);

        // Update selectedPair logic (this is separate and fine)
        if (this.selectedPair && this.selectedPair.symbol === action.payload.symbol) {
          this.selectedPair = { ...this.selectedPair, ...action.payload };
        }
        break;

      case 'UPDATE_MARKET_PRICE':
        newList = this._handleMarketPriceUpdate(currentList, action.payload);
        break;

      default:
        // If we don't know the action, just return the old list
        newList = currentList;
    }

    // This is now the ONLY place in your component 
    // where 'this.topGainers' is ever assigned.
    this.topGainers = newList;
  }

  /**
   * Helper Function: Logic from your old 'updateTopGainers'
   * This is a "pure" function: it receives a list and returns a new list.
   */
  private _handleVmPairUpdate(currentList: any[], result: any): any[] {
    if (!result || typeof result !== 'object' || Array.isArray(result) || !result.symbol) {
      return currentList; // Return the old list unchanged
    }

    const newSymbol = result.symbol;
    // const existingItem = currentList.find(item => item.symbol === newSymbol);
    // if (existingItem) {
    //   // UPDATE: Create a new array, replacing the old item
    //   return currentList.map(item =>
    //     item.symbol === newSymbol ? { ...item, ...result } : item
    //   );
    // } else {
    // ADD: Create a new array with the new item
    const listWithNewItem = [result, ...currentList];

    // Check FIFO limit
    if (listWithNewItem.length > 10) {
      return listWithNewItem.slice(0, -1); // Returns a new array
    } else {
      return listWithNewItem;
    }
    // }
  }

  /**
   * Helper Function: Logic from your old 'marketPriceWs'
   * This is also a "pure" function.
   */
  private _handleMarketPriceUpdate(currentList: any[], result: any): any[] {
    if (!currentList || currentList.length === 0 || !result?.symbol) {
      return currentList; // Return the old list unchanged
    }

    const markPrice = parseFloat(result?.data?.bestBid ?? result?.data?.markPrice);

    // Create a new array with the updated price/percentage
    return currentList.map(item => {
      if (item.symbol !== result.symbol) {
        return item; // Not this item, return it unchanged
      }
      let currentPercentage = markPrice / (item.price / item.change);; // 👈 Remember to fix this logic

      // This is the item, return a new object
      return {
        ...item,
        markPrice: !isNaN(markPrice) ? markPrice : item.markPrice,
        currentPercentage: !isNaN(currentPercentage) ? currentPercentage : item.currentPercentage
      };
    });
  }

  // FIX 1: TrackBy Function for Angular Performance
  trackBySymbol(index: number, item: any): string {
    return item.symbol;
  }
  handleSearchPair(event: any) {
    this.searchPair = event.target.value.toUpperCase().replace('/', '');
  }

  getPairs() {
    this.pairFacade.pair$.subscribe({
      next: (pairs: any) => {
        this.pairs = pairs;
      },
      error: (err: any) => { }
    });
  }
  getRecentGainer() {
    if (!this.auth.signedIn) {
      return
    }
    let param = {
      skip: 0,
      limit: 10
    }
    this.service.getOpportunityHistory({ filter: param })
      .subscribe({
        next: (v: any) => {
          if (v.success) {
            this.topGainers = v.result
          }
        },
        error: (err: any) => {
        }
      })
  }
  downloadExcel(botId: any) {
    this.isDownloading = true;
    this.downloadProgress = 0;

    this.service.downloadOpportunity({ botId }).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.DownloadProgress) {
          // event.loaded and event.total (if server provides Content-Length)
          if (event.total) {
            this.downloadProgress = Math.round(100 * event.loaded / event.total);
          }
        } else if (event.type === HttpEventType.Response) {
          if (event.body) {
            const blob = event.body as Blob;
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);

            a.href = objectUrl;
            a.download = 'Opportunity-History.csv';

            // FIX: Append to body, click, then remove
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
            this.isDownloading = false;
          }
        }
      },
      error: (err) => {
        console.error(err);
        this.isDownloading = false;
      }
    });
  }
  // FIX 3: Pair Selection Logic
  changePair(pair: any) {
    // Immediate UI update
    this.selectedPair = pair;

    // Reset investment amount when switching pairs
    this.investmentAmount = null;

    const newPair = pair.symbol;

    // Avoid reloading if it's strictly the active page pair (optional check)
    if (newPair === this.activePair) return false;

    if (this.activePair) {
      this.botSocket.emit("unSubscribe", this.activePair.replace("_", ""));
    }

    // Find full pair details if available in 'this.pairs'
    const originalPair = this.pairs.find((v: any) => v.symbol === newPair);

    // Use found pair, or fallback to the clicked socket pair object
    const pairToUse = originalPair ? originalPair : pair;

    // Create a safe copy
    const fullPair = { ...pairToUse };
    this.selectedPair = fullPair;
    // Safe string replacement
    if (fullPair.symbol) {
      fullPair.firstCurrencySymbol = fullPair.symbol.replace("USDT", "");
      fullPair.secondCurrencySymbol = "USDT";
    }

    this.service.setTradePair(fullPair);
    this.positionDetails.setPosition({});

    return false;
  }

  createBot() {
    this.submitLoader = true;

    if (!this.selectedPair) {
      this.toastr.error("Please select a pair first");
      return;
    }
    if (!this.investmentAmount || this.investmentAmount <= 0) {
      this.toastr.error("Please enter a valid investment amount");
      return;
    }

    let pairName = this.selectedPair.symbol
    let genDet = {
      pairName: this.selectedPair.symbol,
      firstCurrencySymbol: this.selectedPair.firstCurrencySymbol,
      secondCurrencySymbol: this.selectedPair.secondCurrencySymbol,
      totalBots: 1,
      investment: parseFloat(this.investmentAmount),
      isManual: true,

    }
    let reqData = {
      pairName: pairName,
      pairId: this?.selectedPair?._id,
      exchange: "binance",
      BotType: "VMAP",
      RunStatus: false,
      tradesetting: genDet,
      orderType: "Limit",
      validate: false
    };
    this.toastr.success("Please wait for a while");
    this.service.orderPlace(reqData).subscribe({
      next: (v: any) => {
        this.toastr.success(v.message)
        reqData.validate = false
        this.submitLoader = false;
        this.investmentAmount = 0
      },
      error: (err: any) => {
        this.submitLoader = false;

        reqData.validate = false

        if ([400, 404].includes(err.status)) {
          if (err?.error?.message)
            this.toastr.error(err?.error?.message);
          const validationErrors = err.error.errors;
          if (!isEmpty(validationErrors)) {
            this.serverErrors = validationErrors || {};
            this.toastr.error(err.error.message)
            if (err?.error?.order) {
              this.toastr.error(err.error.order)
            }
          }
        } else if (err.status === 500) {
          this.toastr.error(err.error.message);
        }
      },
      complete: () => { },
    });
  }
  // bot create
  handleTradeChange(e: any): void {
    e.preventDefault()
    let { name, value } = e.target
    if (
      ["investment", "upperPrice", "lowerPrice", "grids", "gridStepPercent", "gridStepValue", "takeprofitRatio",
        "takeprofitCallback", "stopLoss", "buyPercentage", "buyCount", "sellPercentage", "sellCount"].includes(name)
    ) {
      //number 
      const isValidFloat = /^(\d+)?(\.\d{0,10})?$/.test(value) || value === '' || value === '.';
      if (!isValidFloat) return
    }

    //number 
    // cur decimals based on binance
    if (["upperPrice", "lowerPrice", "gridStepValue", "investment"].includes(name)) value = toFixedDown(value, 2)
    // if (["investment"].includes(name)) value = toFixedDown(value, this.pairData?.secondFloatDigit)

    this.investmentAmount = value;
    if (value) {
      this.serverErrors = { ...this.serverErrors, ...{ [name]: "" } };
    }
  }

  filterFloatKeydown(event: KeyboardEvent, fieldName: string): void {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'
    ];
    // allow navigation, shortcuts, etc.
    if (
      allowedKeys.includes(event.key) ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    // only digits or dot
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    const decimalLimit = 2;

    const input = event.target as HTMLInputElement;
    const { selectionStart: start, selectionEnd: end, value } = input;
    if (start === null || end === null) {
      // fall back to default
      return;
    }

    // build what the new value *would* be
    const before = value.slice(0, start);
    const after = value.slice(end);
    const newValue = before + event.key + after;

    // regex: any number of digits, optionally a dot followed by up to decimalLimit digits
    const re = new RegExp(`^\\d*(?:\\.\\d{0,${decimalLimit}})?$`);

    if (!re.test(newValue)) {
      event.preventDefault();
      return;
    }
  }


  toggleChangeSort() {
    if (this.changeSort === '') {
      this.changeSort = 'gainers';
    } else if (this.changeSort === 'gainers') {
      this.changeSort = 'losers';
    } else {
      this.changeSort = '';
    }
    this.sortPairs();
  }

  sortPairs() {
    // Sorting logic applied to 'this.pairs' (Note: usually this applies to topGainers too if you want the table sorted)
    const pairsCopy = [...this.pairs];
    if (this.changeSort === 'gainers') {
      this.pairs = pairsCopy.sort((a, b) => (b.change || 0) - (a.change || 0));
    } else if (this.changeSort === 'losers') {
      this.pairs = pairsCopy.sort((a, b) => (a.change || 0) - (b.change || 0));
    } else {
      this.pairs = pairsCopy;
    }
  }
  getMinutesDiff(dateString: any): number {
    if (!dateString) return 0;
    const created = new Date(dateString).getTime();
    const now = new Date().getTime();
    // Calculate difference in minutes: (Current Time - Created Time) / 60,000 ms
    const diff = Math.floor((now - created) / 60000);
    return diff > 0 ? diff : 0; // Prevent negative numbers
  }
}